import { Loader } from 'components/Loader/Loader';
import {
  OrderInstruction,
  toOrderInstructions,
} from 'components/maps/BundleMap';
import {
  OrderBundle,
  OrderBundleColumn,
} from 'components/OrderBundle/OrderBundle';
import { Dayjs } from 'dayjs';
import { fetchOrdersForMonitor } from 'fetch/orderFetch';
import { InstructionGroup } from 'pages/monitor/MonitorLive/Monitor.types';
import {
  removeInstructionsFromBundle,
  reorderDraftNew,
} from 'pages/oldPlanner/utils/bundleDraft';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Swal, { SweetAlertResult } from 'sweetalert2';
import {
  BundleJson,
  BundleStatus,
  Instruction,
  InstructionType,
} from 'types/bundleTypes';
import { OrderMonitor, Region } from 'types/orderTypes';
import { BundleItemBottomMenu } from './Item/BundleItemBottomMenu';
import { BundleItemTopMenu } from './Item/BundleItemTopMenu';

interface BundleItemProps {
  region: Region;
  activeDate: Dayjs;
  bundle: BundleJson;
  otherBundles: BundleJson[];
  selectedDraftBundles: string[];
  activeBundleMap?: string;
  refetching: boolean;
  updateSelectedDraftBundles: (newSelectedDraftBundles: Array<string>) => void;
  reFetch: () => void;
  onInteractingWithBundleMap: (bundleId: string) => void;
}

export const BundleItem = ({
  updateSelectedDraftBundles,
  reFetch,
  onInteractingWithBundleMap,
  ...props
}: BundleItemProps) => {
  const [orderInstructions, setOrderInstructions] = useState<
    Array<OrderInstruction>
  >([]);
  const [expand, setExpand] = useState(false);
  const [splitBundleActivated, setSplitBundleActivated] = useState(false);
  const [moveToBundleActivated, setMoveToBundleActivated] = useState(false);
  /* Selection for the orders inside the Bundle */
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);

  /* This `currentBundle` will be used to compare if there's any changes on the `props.bundle` */
  /* Without this, the list of Bundle will always reset
   * and this will impacting the Drag&Drop feature.
   *
   * In Drag&Drop feature, if `isDndDraft === true`,
   * we should not change the user Draft (unless the list has changes from another user)
   */
  const currentBundle = useRef(props.bundle);

  /* This `isDoingSomething` will be used for the state of the bundle */
  /* whether it's still doing something or not */
  const isDoingSomething = useRef(false);

  const updateOrderInstruction = useCallback(async () => {
    const orderIdsInBundle = currentBundle.current.instructions.map(
      instruction => instruction.orderId
    );
    let orderListDetail: OrderMonitor[];
    try {
      orderListDetail = await fetchOrdersForMonitor(orderIdsInBundle);
    } catch {
      orderListDetail = [];
    }

    setOrderInstructions(
      toOrderInstructions(currentBundle.current, orderListDetail)
    );
  }, []);

  const pickupInstructionsGroup = () => {
    orderInstructions;

    let group: InstructionGroup = null;

    for (let i = 0; i < orderInstructions.length; i++) {
      const order = orderInstructions[i].order;
      if (!order) {
        break;
      }

      const instr = orderInstructions[i].instruction;

      /* Only add the groupCount with type = Pickup */
      if (instr.type === InstructionType.Pickup) {
        /* First initiate pickupGroup values */
        if (!group) {
          const address = order.originAddress.fullAddressString.replace(
            /\s\s+/g,
            ' '
          );
          group = {
            address,
            instruction: instr,
            index: i,
            count: 0,
          };
        }

        group.count++;
      } else {
        /* Stop the looping when the pickup sequence stopped */
        break;
      }
    }

    if (group?.count <= 1) {
      group = null;
    }

    return group;
  };

  const onSelectDraftBundles = () => {
    const bundleId = currentBundle.current.id;
    const indexOfSelectedBundle = props.selectedDraftBundles.findIndex(
      id => id === bundleId
    );

    const newSelectedDraftBundles = [...props.selectedDraftBundles];
    if (indexOfSelectedBundle === -1) {
      newSelectedDraftBundles.push(bundleId);
    } else {
      newSelectedDraftBundles.splice(indexOfSelectedBundle, 1);
    }
    updateSelectedDraftBundles(newSelectedDraftBundles);
  };

  const onOpenExpand = () => {
    setExpand(true);
  };
  const onCloseExpand = () => {
    setExpand(false);
  };

  const toggleExpand = () => {
    if (expand) {
      onCloseExpand();
    } else {
      onOpenExpand();
    }

    /**
     * Note :
     * After the bundle creation, usually it will adding `map-selection-animation` class into `planner-map-draft-list`.
     * This `planner-map-draft-list` class will mark the bundle with the blinking red whenever new orders added to that bundle.
     * But that blinking is not going away after the creation.
     * So we need to make the functionality to always remove the red blinking after the bundle has been clicked
     */
    const clickedBundle = document.getElementById(props.bundle.id);
    clickedBundle.classList.remove('map-selection-animation');
  };

  const onResetActivatedFeatures = useCallback(() => {
    if (splitBundleActivated) {
      setSplitBundleActivated(false);
    }
    if (moveToBundleActivated) {
      setMoveToBundleActivated(false);
    }
    if (selectedOrders.length > 0) {
      setSelectedOrders([]);
    }
  }, [moveToBundleActivated, selectedOrders.length, splitBundleActivated]);

  const onActivateSplitBundle = () => {
    onResetActivatedFeatures();
    setSplitBundleActivated(true);
    onOpenExpand();
  };

  const onActivateMoveToBundle = () => {
    onResetActivatedFeatures();
    setMoveToBundleActivated(true);
    onOpenExpand();
  };

  const onSelectOrderInstruction = (
    orderInstructions: Array<OrderInstruction>
  ): void => {
    const orders = orderInstructions.map(
      orderInstruction => orderInstruction.order.id
    );
    setSelectedOrders(orders);
  };

  const onStartDoingSomething = () => {
    isDoingSomething.current = true;
  };

  const onStopDoingSomething = () => {
    isDoingSomething.current = false;
  };

  const onDeleteOrderInstruction = async (orderId: string) => {
    if (isDoingSomething.current) {
      /* Do nothing when there is another action still in progress */
      return;
    }

    onStartDoingSomething();
    const needConfirmation =
      currentBundle.current.status !== BundleStatus.Draft;
    let confirmationResult: SweetAlertResult<unknown> = null;

    if (needConfirmation) {
      confirmationResult = await Swal.fire({
        title: `Remove order from`,
        html: `Are you sure you want to remove order(s) <b>${orderId}</b> from bundle <b>${currentBundle.current.id}</b>?<br/> The order will change its status to Ready.`,
        icon: 'warning',
        showCancelButton: true,
        focusCancel: true,
        confirmButtonText: 'Remove order',
        confirmButtonColor: '#d33',
        cancelButtonText: 'Never mind',
        cancelButtonColor: '#ccc',
      });
    }

    if (!needConfirmation || confirmationResult?.value) {
      try {
        await removeInstructionsFromBundle(currentBundle.current, [orderId]);
      } catch (error) {
        Swal.fire('Instruction could not be deleted', undefined, 'error');
      }

      reFetch();
    }

    onStopDoingSomething();
  };

  const reorderTheNewDraft = async (
    instructions: Instruction[]
  ): Promise<BundleJson> => {
    let reorderResult: BundleJson = null;

    try {
      reorderResult = await reorderDraftNew(
        currentBundle.current.id,
        instructions
      );
    } catch (error) {
      Swal.fire({
        title: `Something unexpected occured when reordering the new Draft bundle`,
        text: `${error}`,
        icon: 'error',
      });
    }

    return reorderResult;
  };

  const saveActions = async (orderInstructions: Array<OrderInstruction>) => {
    onStartDoingSomething();

    await reorderTheNewDraft(
      orderInstructions.map(orderInstruction => orderInstruction.instruction)
    );
    reFetch();
    onStopDoingSomething();
  };

  const onSaveDndDraft = async (
    _: BundleJson,
    newOrderInstructions: Array<OrderInstruction>
  ) => {
    await saveActions(newOrderInstructions);
  };

  useEffect(() => {
    updateOrderInstruction().catch();
  }, [updateOrderInstruction]);

  /* Only updating the `currentBundle` when there is changes */
  useEffect(() => {
    const isBundleChanged =
      JSON.stringify(currentBundle.current) !== JSON.stringify(props.bundle);

    if (isBundleChanged) {
      currentBundle.current = props.bundle;

      /* Trigger component update */
      updateOrderInstruction().catch();
      onResetActivatedFeatures();
    }
  }, [onResetActivatedFeatures, props.bundle, updateOrderInstruction]);

  const itemColumns: Array<keyof typeof OrderBundleColumn> = [
    'InstructionType',
    'OrderNumber',
    'Address',
    'WindowTime',
    'EstimateTimeArrival',
  ];

  const isDraft = currentBundle.current.status === BundleStatus.Draft;

  return (
    <div
      key={`planner-bundle-${currentBundle.current.id}`}
      className="planner-bundle"
    >
      {props.refetching && isDoingSomething.current && (
        <div className="planner-bundle-loader">
          <Loader type="spinner" size="large" />
        </div>
      )}
      <BundleItemTopMenu
        region={props.region}
        activeDate={props.activeDate}
        bundle={currentBundle.current}
        isDraft={isDraft}
        checked={props.selectedDraftBundles.includes(
          currentBundle.current.id.toString()
        )}
        orderInstructions={orderInstructions}
        activeBundleMap={props.activeBundleMap}
        splitBundleActivated={splitBundleActivated}
        moveToBundleActivated={moveToBundleActivated}
        onSelectDraftBundles={onSelectDraftBundles}
        toggleExpand={toggleExpand}
        reFetch={reFetch}
        onInteractingWithBundleMap={onInteractingWithBundleMap}
        onActivateSplitBundle={onActivateSplitBundle}
        onActivateMoveToBundle={onActivateMoveToBundle}
        onResetActivatedFeatures={onResetActivatedFeatures}
      />
      {expand && (
        <OrderBundle
          border={false}
          defaultOrderInstructions={orderInstructions}
          columns={itemColumns}
          hasDragAndDrop={!splitBundleActivated && !moveToBundleActivated}
          hasCheckbox={splitBundleActivated || moveToBundleActivated}
          hasDelete={!splitBundleActivated && !moveToBundleActivated}
          bundle={currentBundle.current}
          selectedOrders={selectedOrders}
          pickupsGroup={pickupInstructionsGroup()}
          onSelectOrderInstruction={onSelectOrderInstruction}
          onDeleteOrderInstruction={onDeleteOrderInstruction}
          onSaveDndDraft={onSaveDndDraft}
        />
      )}
      <div className="planner-bundle-instructions">
        <div className="planner-bundle-instruction-container">
          {expand && (
            <BundleItemBottomMenu
              region={props.region}
              bundle={currentBundle.current}
              otherBundles={props.otherBundles}
              isDraft={isDraft}
              splitBundleActivated={splitBundleActivated}
              moveToBundleActivated={moveToBundleActivated}
              selectedOrders={selectedOrders}
              orderInstructions={orderInstructions}
              isDoingSomething={isDoingSomething.current}
              reFetch={reFetch}
              onResetActivatedFeatures={onResetActivatedFeatures}
              onStartDoingSomething={onStartDoingSomething}
              onStopDoingSomething={onStopDoingSomething}
              reorderTheNewDraft={reorderTheNewDraft}
              saveActions={saveActions}
            />
          )}
        </div>
      </div>
    </div>
  );
};
